<script >
import ChartingWorkspaceFinderFiltersPanel from '@/features/charting-next/components/workspace/ChartingWorkspaceFinderFiltersPanel.vue';
import ChartingLeftDrawerSearchesSection from '@/features/charting-next/components/workspace/left-drawer/ChartingLeftDrawerSearchesSection.vue';
import { computed } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    type: Boolean,
    default: false
  },
  entryType: {
    type: String,
    default: ''
  },
  entryValue: {
    type: String,
    default: ''
  },
  tierLabel: {
    type: String,
    default: ''
  },
  draftQuery: {
    type: Object,
    default: null
  },
  appliedSummary: {
    type: Object,
    default: () => ({
      count: 0,
      label: 'Sin filtros',
      chips: []
    })
  },
  selectionOptions: {
    type: Array,
    default: () => []
  },
  restrictedSelections: {
    type: Object,
    default: () => ({})
  },
  canUseSavedSearches: {
    type: Boolean,
    default: false
  },
  canManageSavedSearches: {
    type: Boolean,
    default: false
  },
  savedSearches: {
    type: Array,
    default: () => []
  },
  canSaveCurrentSearch: {
    type: Boolean,
    default: false
  },
  canUseDescriptionSearch: {
    type: Boolean,
    default: false
  },
  canUseFilters: {
    type: Boolean,
    default: false
  },
  mutationError: {
    type: String,
    default: ''
  },
  isReady: {
    type: Boolean,
    default: false
  },
  filters: {
    type: Object,
    default: null
  },
  options: {
    type: Object,
    default: () => ({
      currencies: [],
      countries: [],
      sectors: [],
      industries: []
    })
  },
  filterAccess: {
    type: Object,
    default: () => ({})
  },
  isApplying: {
    type: Boolean,
    default: false
  },
  hasPendingChanges: {
    type: Boolean,
    default: false
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const selected = computed(() => new Set(props.draftQuery?.selections || []));
  const restrictionMessage = selection => {
    const restriction = props.restrictedSelections?.[selection];
    return typeof restriction === 'string' ? restriction : restriction?.message || '';
  };
  const isRestricted = selection => Boolean(restrictionMessage(selection) && !selected.value.has(selection));
  const updateVisibility = visible => {
    if (visible) emit('begin-draft');else emit('cancel-draft');
    emit('update:visible', visible);
  };
  return {
    emit,
    selected,
    restrictionMessage,
    isRestricted,
    updateVisibility
  };
};
__sfc_main.components = Object.assign({
  ChartingLeftDrawerSearchesSection,
  ChartingWorkspaceFinderFiltersPanel
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <Dialog
        :visible="visible"
        modal
        dismissableMask
        closeOnEscape
        mask-class="charting-mobile-fullscreen-mask"
        dialog-class="charting-mobile-fullscreen-dialog"
        :dialog-style="{ width: 'calc(100vw - 2rem)', maxWidth: '74rem' }"
        @update:visible="updateVisibility"
        @show="emit('begin-draft')"
    >
        <template #header>
            <div>
                <div class="text-xs font-semibold uppercase tracking-wide text-muted-color">Explorar</div>
                <div class="mt-1 text-lg font-semibold">Explorar mercado</div>
            </div>
        </template>

        <div class="charting-workspace-dialog-shell">
            <section class="charting-workspace-dialog-summary">
                <div class="flex flex-wrap items-center gap-2">
                    <Tag v-if="tierLabel" :value="tierLabel" severity="secondary" />
                    <Tag :value="appliedSummary.count ? `${appliedSummary.count} filtros activos` : 'Sin filtros'" severity="info" />
                </div>
                <div v-if="appliedSummary.count" class="text-sm text-muted-color">
                    {{ appliedSummary.label }}
                </div>
            </section>

            <div class="charting-workspace-dialog-grid">
                <div class="charting-workspace-dialog-column">
                    <section class="charting-workspace-drawer-section">
                        <div class="text-xs font-semibold uppercase tracking-wide text-muted-color">Filtros principales</div>
                        <p class="mt-2 text-sm text-muted-color">Los valores deben pertenecer a todas las selecciones marcadas. Cada lista conserva el límite de tu plan.</p>
                        <div class="charting-market-selection-grid mt-3">
                            <button
                                v-for="selection in selectionOptions"
                                :key="selection.id"
                                type="button"
                                class="charting-market-selection"
                                :class="{ 'is-active': selected.has(selection.id), 'is-restricted': isRestricted(selection.id) }"
                                :aria-pressed="selected.has(selection.id) ? 'true' : 'false'"
                                :disabled="isRestricted(selection.id)"
                                :title="restrictionMessage(selection.id)"
                                @click="emit('toggle-selection', selection.id)"
                            >
                                {{ selection.label }}
                            </button>
                        </div>
                    </section>

                    <section class="charting-workspace-drawer-section">
                        <div class="flex items-center justify-between gap-3">
                            <div class="text-xs font-semibold uppercase tracking-wide text-muted-color">Descripción</div>
                            <Tag v-if="!canUseDescriptionSearch" value="Restringido" severity="warn" />
                        </div>
                        <label class="sr-only" for="charting-market-description">Palabras en la descripción</label>
                        <InputText
                            id="charting-market-description"
                            :model-value="(draftQuery && draftQuery.description) || ''"
                            class="mt-3 w-full"
                            maxlength="200"
                            placeholder="Bancos, semiconductores, energía..."
                            :disabled="!canUseDescriptionSearch && !((draftQuery && draftQuery.description) || '')"
                            @update:model-value="emit('update-description', $event)"
                        />
                    </section>

                    <ChartingLeftDrawerSearchesSection
                        :entry-type="entryType"
                        :entry-value="entryValue"
                        :can-use-saved-searches="canUseSavedSearches"
                        :can-manage-saved-searches="canManageSavedSearches"
                        :saved-searches="savedSearches"
                        :can-save-current-search="canSaveCurrentSearch"
                        @open-source="emit('open-source', $event)"
                        @create-saved-search="emit('create-saved-search', $event)"
                        @rename-saved-search="emit('rename-saved-search', $event)"
                        @remove-saved-search="emit('remove-saved-search', $event)"
                    />
                </div>

                <div class="charting-workspace-dialog-column">
                    <ChartingWorkspaceFinderFiltersPanel
                        :can-use-filters="canUseFilters"
                        :mutation-error="mutationError"
                        :is-ready="isReady"
                        :filters="filters"
                        :options="options"
                        :filter-access="filterAccess"
                        :is-applying="isApplying"
                        :has-pending-changes="hasPendingChanges"
                        @update:filters="emit('update:filters', $event)"
                    />
                </div>
            </div>
        </div>

        <template #footer>
            <div class="charting-market-dialog-footer">
                <Button label="Limpiar" icon="pi pi-filter-slash" severity="secondary" text :disabled="isApplying" @click="emit('clear-draft')" />
                <span class="charting-market-dialog-footer__spacer"></span>
                <Button label="Cancelar" severity="secondary" outlined :disabled="isApplying" @click="updateVisibility(false)" />
                <Button label="Aplicar" icon="pi pi-check" :loading="isApplying" :disabled="!isReady" @click="emit('apply-query')" />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.charting-workspace-dialog-shell {
    display: flex;
    min-height: 0;
    flex-direction: column;
    gap: 1rem;
}

.charting-workspace-dialog-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.65rem 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--charting-b-border-color);
}

.charting-workspace-dialog-grid {
    display: grid;
    min-height: 0;
    gap: 1rem;
    grid-template-columns: minmax(0, 24rem) minmax(0, 1fr);
}

.charting-workspace-dialog-column {
    display: flex;
    min-height: 0;
    flex-direction: column;
    gap: 1rem;
}

.charting-workspace-dialog-shell ::v-deep .charting-workspace-drawer-section,
.charting-workspace-dialog-shell ::v-deep .charting-workspace-filter-section {
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
}

.charting-workspace-dialog-shell ::v-deep .charting-workspace-filter-section + .charting-workspace-filter-section,
.charting-workspace-dialog-column > * + * {
    border-top: 1px solid var(--charting-b-border-color);
    padding-top: 1rem;
}

.charting-workspace-dialog-column + .charting-workspace-dialog-column {
    border-left: 1px solid var(--charting-b-border-color);
    padding-left: 1rem;
}

.charting-market-selection-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
}

.charting-market-selection {
    border: 1px solid var(--charting-b-border-color);
    border-radius: 999px;
    background: var(--charting-b-panel-bg-strong);
    padding: 0.62rem 0.8rem;
    color: var(--p-text-color);
    font-weight: 700;
    cursor: pointer;
}

.charting-market-selection.is-active {
    border-color: var(--brand-primary);
    background: color-mix(in srgb, var(--brand-primary), transparent 84%);
    color: var(--brand-primary);
}

.charting-market-selection.is-restricted {
    opacity: 0.48;
    cursor: not-allowed;
}

.charting-market-dialog-footer {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 0.65rem;
}

.charting-market-dialog-footer__spacer {
    flex: 1 1 auto;
}

@media (max-width: 1023px) {
    .charting-workspace-dialog-shell { max-height: none; }
    .charting-workspace-dialog-grid { grid-template-columns: 1fr; }
    .charting-workspace-dialog-column { overflow: visible; padding-right: 0; }
    .charting-workspace-dialog-column + .charting-workspace-dialog-column { border-left: 0; border-top: 1px solid var(--charting-b-border-color); padding: 1rem 0 0; }
}

@media (max-width: 520px) {
    .charting-market-selection-grid { grid-template-columns: 1fr; }
    .charting-market-dialog-footer { flex-wrap: wrap; }
    .charting-market-dialog-footer__spacer { display: none; }
}
</style>
